import { DataQuality, FieldType, Option, Question } from './data-quality.model';
import { AttributeValue } from './section-definition.model';

export class CimSubstationForSave {
  id: number | null = null;
  name: string = '';
  number: string = '';
  substationTemplateId: number | null = null;
  baseVoltageId: number | null = null;
  subGeographicalRegionId: number | null = null;
  parentSubstations: ParentSubstation[] | null = null;
  meterNumber: string = '';
  meterBrandId: number | null = null;
  latitude: number | null = null;
  longitude: number | null = null;

  constructor(data?: CimSubstationForSave) {
    this.id = data?.id ?? null;
    this.name = data?.name ?? '';
    this.number = data?.number ?? '';
    this.substationTemplateId = data?.substationTemplateId ?? null;
    this.baseVoltageId = data?.baseVoltageId ?? null;
    this.subGeographicalRegionId = data?.subGeographicalRegionId ?? null;
    this.parentSubstations = data?.parentSubstations ?? null;
    this.meterNumber = data?.meterNumber ?? '';
    this.meterBrandId = data?.meterBrandId ?? null;
    this.latitude = data?.latitude ?? null;
    this.longitude = data?.longitude ?? null;
  }
}

export interface ParentSubstation {
  parentSubstationId: number | null;
  parentSubstationACLineId: number | null;
}

export class BaseFilter {
  searchValue: string = '';
  subGeographicalRegionIds: number[] = [];
}

export class CimSubstationFilter extends BaseFilter {
  substationTemplateId: number = 0;
  searchType: SearchType = SearchType.Name;
}

export enum SearchType {
  Name = 1,
  Number = 2,
}

export interface IncomingAcLineDetails {
  parentSubstationId: number;
  parentSubstationName: string;
  parentACLineId: number;
  parentACLineName: string;
}

export class EditCimSubstation {
  id: number;
  mrid: string;
  name: string;
  number: string;
  subGeographicalRegion: string;
  subGeographicalRegionId: number;
  geographicalRegion: string;
  geographicalRegionId: number;
  slrn: string;
  latitude: number | null;
  longitude: number | null;
  auditedStatus: string;
  auditedBy: string;
  auditedDateTime: string;
  validationStatus: string;
  validatedBy: string;
  validationDate: string;
  firstCapturedUser: string;
  lastUpdatedUser: string;
  lastUpdateTime: string;
  creationDate: string;
  reassignedBy: string;
  assignedBy: string;
  assignedUserId: number = 0;
  reviewStatus: string;
  assetStatus: string;
  dataQualityId: number | null = null;
  incomingAcLines: IncomingAcLineDetails[] = [];
  pendingIncomingAcLines: IncomingAcLineDetails[] = [];
  gisReviewStatus: string;
  gisReviewedBy?: string;
  gisReviewDate?: string;
  gisReviewUpdateStatus?: string;
  numberOfUprisers: number;
  voltageClass: string;
  reviewedBy?: string;
  reviewedDate?: string;
  meterId: number | null = null;
  meterCondition: string;
  meterType: string;
  meterCommunicationStatus: string;
  meterBrand: string;
  meterBrandId: number | null = null;
  meterNumber: string;
  proposedLocation: string;
  isCableVisible: boolean | null = null;
  isCableLengthEnough: boolean | null = null;
  constructor(data?: any) {
    if (data) {
      this.id = data.id ?? 0;
      this.mrid = data.mrid ?? '';
      this.name = data.name ?? '';
      this.number = data.number ?? '';
      this.subGeographicalRegion = data.subGeographicalRegion ?? '';
      this.geographicalRegion = data.geographicalRegion ?? '';
      this.slrn = data.slrn ?? '';
      this.latitude = data.latitude ?? null;
      this.longitude = data.longitude ?? null;
      this.auditedStatus = data.auditedStatus ?? '';
      this.firstCapturedUser = data.firstCapturedUser ?? '';
      this.lastUpdatedUser = data.lastUpdatedUser ?? '';
      this.lastUpdateTime = data.lastUpdateTime ?? '';
      this.creationDate = data.creationDate ?? '';
      this.auditedBy = data.auditedBy ?? '';
      this.auditedDateTime = data.auditedDateTime ?? '';
      this.reassignedBy = data.reassignedBy ?? '';
      this.assignedBy = data.assignedBy ?? '';
      this.reviewStatus = data.reviewStatus ?? '';
      this.assignedUserId = data.assignedUserId ?? 0;
      this.geographicalRegionId = data.geographicalRegionId ?? 0;
      this.subGeographicalRegionId = data.subGeographicalRegionId ?? 0;
      this.dataQualityId = data.dataQualityId ?? null;
      this.assetStatus = data.assetStatus ?? '';
      this.incomingAcLines = data.incomingAcLines ?? [];
      this.pendingIncomingAcLines = data.pendingIncomingAcLines ?? [];
      this.gisReviewStatus = data.gisReviewStatus ?? '';
      this.gisReviewedBy = data.gisReviewedBy ?? '';
      this.gisReviewDate = data.gisReviewDate ?? '';
      this.gisReviewUpdateStatus = data.gisReviewUpdateStatus ?? '';
      this.validatedBy = data.validatedBy ?? '';
      this.validationDate = data.validationDate ?? '';
      this.validationStatus = data.validationStatus ?? '';
      this.numberOfUprisers = data.numberOfUprisers ?? 0;
      this.reviewedBy = data.reviewedBy ?? '';
      this.reviewedDate = data.reviewedDate ?? '';
      this.meterId = data.meterId ?? null;
      this.meterCondition = data.meterCondition ?? '';
      this.meterType = data.meterType ?? '';
      this.meterCommunicationStatus = data.meterCommunicationStatus ?? '';
      this.meterBrand = data.meterBrand ?? '';
      this.meterBrandId = data.meterBrandId ?? null;
      this.meterNumber = data.meterNumber ?? '';
      this.proposedLocation = data.proposedLocation ?? '';
      this.isCableVisible = data.isCableVisible ?? null;
      this.isCableLengthEnough = data.isCableLengthEnough ?? null;
    }
  }
}

export interface SubstationHistoryDataOut {
  timestamp: Date;
  user: string;
  userRole: string;
  userEmail: string;
  previousName: string | null;
  newName: string | null;
  previousNumber: string | null;
  newNumber: string | null;
  previousSubGeographicalRegion: string | null;
  newSubGeographicalRegion: string | null;
  previousIncomingACLines: string | null;
  newIncomingACLines: string | null;
}

export class CimSubstationDataQuality {
  dataQuality: DataQuality;
  constructor(templateName: string, imageAttributes: AttributeValue[]) {
    let slrnQuestion = new Question({
      label: templateName + ' SLRN Value Matches Picture',
      options: createDefaultOptions(),
      fieldType: 'Select',
    });
    let serialNoQuestion = new Question({
      label: templateName + ' Serial Number Value Matches Picture',
      options: createDefaultOptions(),
      fieldType: 'Select',
    });
    let meterNoQuestion = new Question({
      label: templateName + ' Meter Number Value Matches Picture',
      options: createDefaultOptions(),
      fieldType: 'Select',
    });

    let assignQuestion = new Question({
      label: 'Assign to User',
      fieldType: 'Select',
      options: createAssignOptions(),
    });

    let reasonQuestion = new Question({
      label: 'Reason for Rejection',
      fieldType: 'Text',
    });

    let ssQuestions = [slrnQuestion, serialNoQuestion, meterNoQuestion];

    if (imageAttributes.length > 0) {
      let imageOptions: Option[] = [];
      imageAttributes.forEach((x) => {
        imageOptions.push(createImageOption(x));
      });

      imageOptions.push(
        new Option({
          text: 'All Images Were Captured Correctly',
          isSelected: true,
          imagesSelected: 'None',
        }),
      );

      imageOptions.push(
        new Option({
          text: 'All Images Should Be Retaken',
          isSelected: false,
          imagesSelected: 'All',
        }),
      );

      let imagesQuestion = new Question({
        label: 'Picture(s) to Retake',
        options: imageOptions,
        fieldType: 'Checkbox',
      });

      ssQuestions.push(imagesQuestion);
    }

    ssQuestions.push(assignQuestion);
    ssQuestions.push(reasonQuestion);

    this.dataQuality = new DataQuality({ questions: ssQuestions });
  }
}

function createDefaultOptions(): Option[] {
  return [
    new Option({ text: 'Yes', isSelected: false }),
    new Option({ text: 'No', isSelected: false }),
    new Option({ text: 'Image not clear', isSelected: false }),
    new Option({ text: 'No image', isSelected: false }),
  ];
}

function createImageOption(attr: AttributeValue) {
  return new Option({
    text: attr.name,
    isSelected: false,
    imagesSelected: 'Partial',
  });
}

function createAssignOptions(): Option[] {
  return [
    new Option({ text: 'Data Collector', isSelected: false }),
    new Option({ text: 'Field Supervisor', isSelected: false }),
  ];
}
